<!-- app/javascript/dashboard/modules/tasks/components/TaskInlineCreate.vue -->
<script setup>
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import ContactAPI from 'dashboard/api/contacts';
import SmartTitleInput from './SmartTitleInput.vue';
import { buildDueIso } from '../helpers/dueDate';

const props = defineProps({
  prefill: {
    type: Object,
    default: () => ({}),
  },
  hideContact: { type: Boolean, default: false },
});

const emit = defineEmits(['created', 'cancel']);

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const agents = useMapGetter('agents/getAgents');
const accountLabels = useMapGetter('labels/getLabels');
const contactCompanySeparator = '·';

function todayDateString() {
  const today = new Date();
  const offset = today.getTimezoneOffset() * 60000;
  return new Date(today.getTime() - offset).toISOString().slice(0, 10);
}

// contato
const editingContact = ref(false);
const contactInputRef = ref(null);
const contactSearch = ref('');
const contactResults = ref([]);
const selectedContact = ref(null);
const showContactDropdown = ref(false);
const contactHighlightedIndex = ref(0);
const isSelectingContact = ref(false);
const displayName = computed(
  () => selectedContact.value?.company_name || selectedContact.value?.name || ''
);

function normalizeContact(c) {
  return {
    ...c,
    company_name:
      c.company_name ?? c.additional_attributes?.company_name ?? null,
  };
}

selectedContact.value = props.prefill.contact
  ? normalizeContact(props.prefill.contact)
  : null;

async function searchContacts(query) {
  if (!query || query.length < 1) {
    contactResults.value = [];
    showContactDropdown.value = false;
    return;
  }
  try {
    const { data } = await ContactAPI.search(query, 1, 'name');
    contactResults.value = (data?.payload || []).map(normalizeContact);
    contactHighlightedIndex.value = 0;
    showContactDropdown.value = contactResults.value.length > 0;
  } catch {
    contactResults.value = [];
  }
}

watch(contactSearch, val => {
  if (editingContact.value) searchContacts(val);
});

function openContactEdit() {
  contactSearch.value = displayName.value;
  editingContact.value = true;
  nextTick(() => {
    contactInputRef.value?.focus();
    contactInputRef.value?.select();
  });
}

function cancelContactEdit() {
  if (isSelectingContact.value) return;
  editingContact.value = false;
  contactSearch.value = '';
  contactResults.value = [];
  showContactDropdown.value = false;
}

function selectContact(contact) {
  isSelectingContact.value = false;
  selectedContact.value = contact;
  showContactDropdown.value = false;
  editingContact.value = false;
}

// estado da nova tarefa
const title = ref('');
const description = ref('');
const smartPendingFields = ref({});
const priority = ref('no_priority');
const taskType = ref('general');
const assigneeId = ref(
  props.prefill.assignee_id ?? currentUser.value?.id ?? null
);
const isSubmitting = ref(false);

// prazo
const dueDateRef = ref(null);
const editingDue = ref(false);
const dueDate = ref(props.prefill.due_date ?? todayDateString());
const dueTime = ref(null);

// menus
const showAssigneeMenu = ref(false);
const showTypeMenu = ref(false);
const showPriorityMenu = ref(false);
const showLabelMenu = ref(false);
const labelSearch = ref('');
const labelSearchRef = ref(null);
const selectedLabels = ref([]);

function onSmartPendingFields(fields) {
  smartPendingFields.value = fields;
  if (fields.description !== null) description.value = fields.description;
  if (fields.priority) priority.value = fields.priority;
  if (fields.assignee_id) assigneeId.value = fields.assignee_id;
  if (fields.task_type) taskType.value = fields.task_type;
  if (fields.label_list?.length) selectedLabels.value = fields.label_list;
  if (fields.due_date) {
    dueDate.value = fields.due_date;
    if (fields.due_time) dueTime.value = fields.due_time;
  }
  if (
    fields.contact_id &&
    (!selectedContact.value || selectedContact.value.id !== fields.contact_id)
  ) {
    selectedContact.value = { id: fields.contact_id, name: '' };
  }
}

// exatamente os mesmos dados do TaskListItem
const TASK_TYPES = [
  { key: 'general', label: 'Geral', icon: 'i-lucide-circle-dot' },
  { key: 'call', label: 'Ligação', icon: 'i-lucide-phone' },
  { key: 'email', label: 'E-mail', icon: 'i-lucide-mail' },
  { key: 'meeting', label: 'Reunião', icon: 'i-lucide-calendar' },
  { key: 'follow_up', label: 'Follow-up', icon: 'i-lucide-refresh-cw' },
  { key: 'whatsapp', label: 'WhatsApp', icon: 'i-lucide-message-circle' },
  { key: 'contract', label: 'Contrato', icon: 'i-lucide-file-signature' },
  { key: 'payment', label: 'Pagamento', icon: 'i-lucide-banknote' },
];

const PRIORITY_OPTIONS = [
  {
    key: 'urgent',
    label: 'Urgente',
    bg: 'bg-n-ruby-2',
    text: 'text-n-ruby-11',
    dot: 'text-n-ruby-9',
  },
  {
    key: 'high',
    label: 'Prioridade',
    bg: 'bg-n-amber-2',
    text: 'text-n-amber-11',
    dot: 'text-n-amber-9',
  },
  {
    key: 'normal',
    label: 'Normal',
    bg: 'bg-n-blue-2',
    text: 'text-n-blue-11',
    dot: 'text-n-blue-9',
  },
  {
    key: 'no_priority',
    label: 'Baixa',
    bg: 'bg-n-surface-2',
    text: 'text-n-slate-10',
    dot: 'text-n-slate-8',
  },
];

const PRIORITY_BAR = {
  urgent: { class: 'bg-n-ruby-9' },
  high: { class: 'bg-n-amber-9' },
  normal: { class: 'bg-n-blue-9' },
  no_priority: { style: { backgroundColor: '#777B84' } },
};

const priorityBarClass = computed(
  () => PRIORITY_BAR[priority.value]?.class ?? ''
);
const priorityBarStyle = computed(
  () => PRIORITY_BAR[priority.value]?.style ?? {}
);

const priorityPlaceholder = 'Prioridade';

const currentType = computed(
  () => TASK_TYPES.find(type => type.key === taskType.value) ?? TASK_TYPES[0]
);
const currentPriority = computed(
  () => PRIORITY_OPTIONS.find(p => p.key === priority.value) ?? null
);
const selectedAssignee = computed(
  () => agents.value?.find(a => a.id === assigneeId.value) ?? null
);

const formattedDue = computed(() => {
  if (!dueDate.value) return null;
  // 'YYYY-MM-DD' em new Date() vira meia-noite UTC e mostrava o dia anterior
  return new Date(buildDueIso(dueDate.value)).toLocaleDateString('pt-BR', {
    month: 'short',
    day: 'numeric',
  });
});

const filteredLabels = computed(() => {
  const q = labelSearch.value.toLowerCase();
  return (accountLabels.value ?? []).filter(l =>
    l.title.toLowerCase().includes(q)
  );
});

function isLabelActive(lbl) {
  return selectedLabels.value.includes(lbl);
}

const { avatarColor, initials: agentInitials } = useAvatarColor();
function initials(name) {
  return agentInitials(name ?? '') || '?';
}

function closeMenus() {
  showAssigneeMenu.value = false;
  showTypeMenu.value = false;
  showPriorityMenu.value = false;
  showLabelMenu.value = false;
}

function toggleAssigneeMenu(e) {
  e.stopPropagation();
  showAssigneeMenu.value = !showAssigneeMenu.value;
  showTypeMenu.value = false;
  showPriorityMenu.value = false;
}
function toggleTypeMenu(e) {
  e.stopPropagation();
  showTypeMenu.value = !showTypeMenu.value;
  showAssigneeMenu.value = false;
  showPriorityMenu.value = false;
}
function togglePriorityMenu(e) {
  e.stopPropagation();
  showPriorityMenu.value = !showPriorityMenu.value;
  showAssigneeMenu.value = false;
  showTypeMenu.value = false;
}

function openLabelMenu(e) {
  e.stopPropagation();
  showLabelMenu.value = true;
  labelSearch.value = '';
  nextTick(() => labelSearchRef.value?.focus());
}

function toggleLabel(lbl, e) {
  e.stopPropagation();
  const idx = selectedLabels.value.indexOf(lbl);
  if (idx === -1) selectedLabels.value.push(lbl);
  else selectedLabels.value.splice(idx, 1);
}

function removeTag(tag, e) {
  e.stopPropagation();
  selectedLabels.value = selectedLabels.value.filter(l => l !== tag);
}

function startEditDue(e) {
  e.stopPropagation();
  editingDue.value = true;
  nextTick(() => dueDateRef.value?.showPicker?.());
}

function commitDue(e) {
  dueDate.value = e.target.value;
  editingDue.value = false;
}

async function submit() {
  if (!title.value.trim() || isSubmitting.value) return;
  isSubmitting.value = true;
  const prefill = { ...props.prefill };
  delete prefill.contact;
  const result = await store.dispatch('tasks/createTask', {
    title: title.value.trim(),
    description: description.value.trim() || null,
    priority: priority.value,
    task_type: taskType.value,
    assignee_id: assigneeId.value || null,
    label_list: selectedLabels.value.length ? selectedLabels.value : undefined,
    // a API não conhece due_time: sem juntar aqui o horário digitado se perdia
    due_date: buildDueIso(dueDate.value, dueTime.value),
    contact_id: selectedContact.value?.id || null,
    ...prefill,
  });
  isSubmitting.value = false;
  if (result.success) emit('created', result.data);
}

function onContactKeydown(e) {
  if (!editingContact.value) return;
  if (document.activeElement !== contactInputRef.value) return;
  if (e.key === 'Escape') {
    isSelectingContact.value = true;
    cancelContactEdit();
    submit();
  } else if (e.key === 'ArrowDown') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.min(
      contactHighlightedIndex.value + 1,
      contactResults.value.length - 1
    );
  } else if (e.key === 'ArrowUp') {
    e.preventDefault();
    contactHighlightedIndex.value = Math.max(
      contactHighlightedIndex.value - 1,
      0
    );
  } else if (e.key === 'Enter') {
    e.preventDefault();
    const item = contactResults.value[contactHighlightedIndex.value];
    isSelectingContact.value = true;
    if (item) {
      selectContact(item);
    }
    submit();
  }
}

function focusContactSearch() {
  editingContact.value = true;
  contactSearch.value = '';
  nextTick(() => {
    contactInputRef.value?.focus();
    contactInputRef.value?.select();
  });
}

function onTitleKeydown(e) {
  if (e.key === 'Enter') {
    e.preventDefault();
    if (title.value.trim()) {
      if (props.hideContact) submit();
      else focusContactSearch();
    }
  }
  if (e.key === 'Escape') emit('cancel');
}

function onTitleBlur() {
  setTimeout(() => {
    if (editingContact.value) return;
    if (title.value.trim()) submit();
    else emit('cancel');
  }, 200);
}

onMounted(() => {
  window.addEventListener('keydown', onContactKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onContactKeydown);
});
</script>

<template>
  <div
    class="task-drag-item flex flex-col md:flex-row md:items-stretch border-b border-n-weak hover:bg-n-alpha-1 group relative"
    :class="[hideContact ? 'min-h-[40px]' : 'min-h-[52px] ml-2']"
    @click="closeMenus"
  >
    <div class="flex items-center md:contents">
      <!-- Borda lateral de prioridade (igual TaskListItem) -->
      <span
        v-if="!hideContact"
        class="w-1 flex-shrink-0 self-stretch rounded-sm"
        :class="priorityBarClass"
        :style="priorityBarStyle"
      />

      <!-- Grip placeholder -->
      <span
        v-if="!hideContact"
        class="flex-shrink-0 self-center px-1 opacity-0 text-n-slate-9"
      >
        <span class="i-lucide-grip-vertical size-4" />
      </span>

      <!-- Checkbox placeholder -->
      <button
        v-if="!hideContact"
        class="size-4 min-w-4 flex-shrink-0 rounded self-center mr-3 flex items-center justify-center border border-n-slate-6 p-0 hover:border-n-teal-6 hover:bg-n-teal-2"
        tabindex="-1"
      />

      <!-- ESQUERDA: título (input) + sublinha vazia -->
      <div
        class="flex-1 min-w-0 pr-2 flex flex-col justify-center"
        :class="hideContact ? 'py-1.5 pl-[52px]' : 'py-2'"
      >
        <SmartTitleInput
          v-model="title"
          :placeholder="t('TASKS.FORM.TITLE_PLACEHOLDER')"
          focus-at-end
          class="text-sm font-medium text-n-slate-12 border-b border-n-weak pb-0.5"
          @update:pending-fields="onSmartPendingFields"
          @submit="onTitleKeydown({ key: 'Enter', preventDefault: () => {} })"
          @cancel="emit('cancel')"
          @blur="onTitleBlur"
          @click.stop
        />

        <!-- Sublinha: contato · labels — igual ao TaskListItem -->
        <div
          v-if="!hideContact || selectedLabels.length"
          class="flex items-center gap-1 overflow-hidden"
        >
          <!-- Contato clicável / editável inline -->
          <div v-if="!hideContact" class="relative shrink-0" @click.stop>
            <input
              v-if="editingContact"
              ref="contactInputRef"
              v-model="contactSearch"
              :placeholder="t('TASKS.FORM.CONTACT_PLACEHOLDER')"
              class="text-xs text-n-slate-12 bg-transparent border-0 outline-none focus:outline-none w-36 placeholder:text-n-slate-9"
              @blur="cancelContactEdit"
            />
            <button
              v-else
              class="text-xs text-n-slate-11 hover:text-n-slate-12 transition-colors"
              @click.stop="openContactEdit"
            >
              {{ displayName || t('TASKS.FORM.CONTACT_PLACEHOLDER') }}
            </button>
            <div
              v-if="showContactDropdown && contactResults.length"
              class="absolute left-0 top-6 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl py-1 max-h-44 overflow-y-auto w-52"
              @mousedown.prevent
            >
              <button
                v-for="(c, idx) in contactResults"
                :key="c.id"
                type="button"
                class="w-full text-left px-3 py-2 text-xs transition-colors"
                :class="
                  idx === contactHighlightedIndex
                    ? 'text-n-brand font-medium bg-n-alpha-1'
                    : 'text-n-slate-12 hover:bg-n-alpha-1'
                "
                @click="
                  selectContact(c);
                  submit();
                "
              >
                {{ c.name
                }}<span v-if="c.company_name" class="text-n-slate-10 ml-1">
                  {{ contactCompanySeparator }} {{ c.company_name }}
                </span>
              </button>
            </div>
          </div>

          <template v-if="selectedLabels.length">
            <span
              v-for="label in selectedLabels"
              :key="label"
              class="group/tag inline-flex items-center text-[11px] font-medium px-1.5 py-px rounded-full shrink-0 bg-n-blue-2 text-n-blue-11"
            >
              {{ label }}
              <span
                class="overflow-hidden transition-all duration-150 max-w-0 group-hover/tag:max-w-[12px] flex items-center justify-center"
              >
                <button
                  class="flex items-center justify-center hover:bg-black/10 rounded-full ml-[2px]"
                  @click="removeTag(label, $event)"
                >
                  <span class="i-lucide-x size-[8px] flex-shrink-0" />
                </button>
              </span>
            </span>
          </template>

          <!-- Botão adicionar label -->
          <div class="relative shrink-0" @click.stop>
            <button
              class="opacity-0 group-hover:opacity-60 hover:opacity-100 transition-opacity"
              @click="openLabelMenu"
            >
              <span class="i-lucide-tag size-3 text-n-slate-10" />
            </button>
            <div
              v-if="showLabelMenu"
              class="absolute left-0 top-5 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-44 overflow-hidden"
            >
              <div class="p-1.5 border-b border-n-weak">
                <input
                  ref="labelSearchRef"
                  v-model="labelSearch"
                  class="w-full text-xs px-2 py-1 rounded-lg bg-n-surface-2 text-n-slate-12 placeholder:text-n-slate-9 outline-none"
                  :placeholder="t('TASKS.TAGS.SEARCH')"
                  @click.stop
                />
              </div>
              <div class="max-h-48 overflow-y-auto py-1">
                <button
                  v-for="lbl in filteredLabels"
                  :key="lbl.id"
                  class="w-full flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-n-alpha-1 transition-colors"
                  :class="
                    isLabelActive(lbl.title)
                      ? 'text-n-slate-12 font-medium'
                      : 'text-n-slate-11'
                  "
                  @click="toggleLabel(lbl.title, $event)"
                >
                  <span
                    class="size-2.5 rounded-full flex-shrink-0"
                    :style="{ backgroundColor: lbl.color }"
                  />
                  <span class="truncate">{{ lbl.title }}</span>
                  <span
                    v-if="isLabelActive(lbl.title)"
                    class="i-lucide-check size-3 ml-auto flex-shrink-0 text-n-brand"
                  />
                </button>
                <div
                  v-if="filteredLabels.length === 0"
                  class="px-3 py-3 text-xs text-n-slate-9 text-center"
                >
                  {{ t('TASKS.TAGS.EMPTY') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- DIREITA: exatamente igual ao TaskListItem -->
    <div
      class="flex items-center gap-2 pr-3 pb-2 flex-wrap md:pb-0 md:flex-shrink-0 md:flex-nowrap"
      :class="hideContact ? 'pl-[52px] md:pl-0' : 'pl-14 md:pl-0'"
    >
      <!-- Prazo -->
      <div class="relative">
        <button
          v-if="formattedDue && !editingDue"
          class="flex items-center gap-1 text-xs text-n-slate-10"
          @click="startEditDue"
        >
          <span class="i-lucide-calendar size-4.5" />
          {{ formattedDue }}
        </button>
        <button
          v-else-if="!editingDue"
          class="text-n-slate-9 opacity-0 group-hover:opacity-100 transition-opacity"
          @click="startEditDue"
        >
          <span class="i-lucide-calendar size-4.5" />
        </button>
        <input
          v-if="editingDue"
          ref="dueDateRef"
          type="date"
          class="absolute right-0 top-0 opacity-0 w-0 h-0"
          @change="commitDue"
          @blur="editingDue = false"
          @click.stop
        />
      </div>

      <!-- Prioridade -->
      <div class="relative">
        <button
          class="flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full transition-colors"
          :class="
            currentPriority && currentPriority.key !== 'no_priority'
              ? [currentPriority.bg, currentPriority.text]
              : 'opacity-0 group-hover:opacity-60 bg-n-surface-2 text-n-slate-10'
          "
          @click="togglePriorityMenu"
        >
          <span
            class="i-lucide-flag size-2.5 flex-shrink-0"
            :class="currentPriority?.dot ?? 'text-n-slate-8'"
          />
          {{
            currentPriority && currentPriority.key !== 'no_priority'
              ? currentPriority.label
              : priorityPlaceholder
          }}
        </button>
        <div
          v-if="showPriorityMenu"
          class="absolute right-0 top-7 z-30 bg-n-background border border-n-weak rounded-xl shadow-xl w-36 overflow-hidden py-1"
          @click.stop
        >
          <button
            v-for="opt in PRIORITY_OPTIONS"
            :key="opt.key"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1 transition-colors"
            :class="
              priority === opt.key
                ? 'font-medium ' + opt.text
                : 'text-n-slate-12'
            "
            @click="
              priority = opt.key;
              showPriorityMenu = false;
            "
          >
            <span class="i-lucide-flag size-3 flex-shrink-0" :class="opt.dot" />
            {{ opt.label }}
          </button>
        </div>
      </div>

      <!-- Ícone descrição (placeholder) -->
      <span
        class="i-lucide-align-left size-4.5 text-n-slate-9 opacity-0 group-hover:opacity-30 transition-opacity flex-shrink-0"
      />

      <!-- Assignee -->
      <div class="relative flex-shrink-0">
        <button
          class="size-9 rounded-full flex items-center justify-center hover:opacity-80 transition-all flex-shrink-0"
          :style="
            selectedAssignee?.thumbnail
              ? {
                  backgroundImage: `url(${selectedAssignee.thumbnail})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                }
              : selectedAssignee
                ? { backgroundColor: avatarColor(selectedAssignee) }
                : {}
          "
          :class="!selectedAssignee ? 'bg-n-surface-2' : ''"
          @click="toggleAssigneeMenu"
        >
          <span
            v-if="selectedAssignee && !selectedAssignee.thumbnail"
            class="text-[11px] font-bold text-white"
          >
            {{ initials(selectedAssignee.name) }}
          </span>
          <span
            v-else-if="!selectedAssignee"
            class="i-lucide-user-plus size-4.5 text-n-slate-9"
          />
        </button>
        <div
          v-if="showAssigneeMenu"
          class="absolute right-0 top-10 z-30 bg-n-background border border-n-weak rounded-lg shadow-xl w-52 overflow-hidden"
          @click.stop
        >
          <button
            class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-11 hover:bg-n-alpha-1 whitespace-nowrap"
            @click="
              assigneeId = null;
              showAssigneeMenu = false;
            "
          >
            <span
              class="i-lucide-user-x size-3.5 text-n-slate-9 flex-shrink-0"
            />
            {{ t('TASKS.MODAL.UNASSIGN') }}
          </button>
          <div class="border-t border-n-weak" />
          <button
            v-for="agent in agents"
            :key="agent.id"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm text-n-slate-12 hover:bg-n-alpha-1"
            @click="
              assigneeId = agent.id;
              showAssigneeMenu = false;
            "
          >
            <div
              class="size-5 rounded-full bg-n-surface-2 overflow-hidden flex items-center justify-center flex-shrink-0"
            >
              <img
                v-if="agent.thumbnail"
                :src="agent.thumbnail"
                class="size-full object-cover"
              />
              <span
                v-else
                class="text-[9px] font-bold text-white size-full flex items-center justify-center"
                :style="{ backgroundColor: avatarColor(agent) }"
              >
                {{ initials(agent.name) }}
              </span>
            </div>
            <span class="truncate">{{ agent.name }}</span>
          </button>
        </div>
      </div>

      <!-- Tipo -->
      <div class="relative flex-shrink-0">
        <button
          class="flex items-center justify-center size-9 rounded hover:bg-n-alpha-2 transition-colors"
          :title="currentType.label"
          @click="toggleTypeMenu"
        >
          <span class="size-7 text-[#8b8d98]" :class="[currentType.icon]" />
        </button>
        <div
          v-if="showTypeMenu"
          class="absolute right-0 top-7 z-30 bg-n-background border border-n-weak rounded-lg shadow-xl w-36 overflow-hidden"
          @click.stop
        >
          <button
            v-for="type in TASK_TYPES"
            :key="type.key"
            class="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-n-alpha-1"
            :class="
              type.key === taskType
                ? 'text-n-brand font-medium'
                : 'text-n-slate-12'
            "
            @click="
              taskType = type.key;
              showTypeMenu = false;
            "
          >
            <span class="size-4 flex-shrink-0" :class="[type.icon]" />
            {{ type.label }}
          </button>
        </div>
      </div>

      <!-- Salvar (no lugar do botão abrir) -->
      <button
        class="size-9 flex items-center justify-center rounded border border-n-container bg-n-background text-n-slate-9 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-n-alpha-2 flex-shrink-0 disabled:opacity-30"
        :disabled="!title.trim() || isSubmitting"
        @click.stop="submit"
      >
        <span class="i-lucide-check size-6" />
      </button>
    </div>
  </div>
</template>
